/**
 * SitePick front — 取置き（24 時間。reservation-notify）の他ユーザー向け文言（純関数）。
 *
 * お気に入り（`favorites.js`）・比較（`compare.js`）・`lock-notice.js` が共通で使う。
 * PHP の `sitepick_hold_notice( $state, $context )`（inc/helpers.php）と**同じ文**を出すためだけのモジュール。
 * DOM・window に触らない（Node の単体テストでそのまま読める）。
 *
 * - 対象は `status === "HELD"`、または `status === "RESERVED"` かつ `hold_active`（holder の購入手続き中）。
 *   後者でも「約 N 分後」は出さない（lock が解けても HELD に戻るため事実と違う）。
 * - 残り時間は core の `hold_hours_left`（時間・切り上げ）をそのまま埋める。JS で期限を計算しない。
 *   1 時間以下（期限を過ぎて後始末待ちの 0 も含む）は「あと 1 時間以内」、値が無ければ時間を言わない。
 */

const FALLBACK = {
  holdNotice: "現在、取置き中です（あと約 %d 時間）。取置きは最長 24 時間で、期限が切れるか取り消されると再び購入できます。",
  holdNoticeSoon: "現在、取置き中です（あと 1 時間以内）。取置きは最長 24 時間で、期限が切れるか取り消されると再び購入できます。",
  holdNoticeUnknown: "現在、取置き中です。取置きは最長 24 時間で、期限が切れるか取り消されると再び購入できます。",
  holdNoticeCard: "取置き中（あと約 %d 時間）",
  holdNoticeCardSoon: "取置き中（あと 1 時間以内）",
  holdNoticeCardUnknown: "取置き中",
};

/**
 * @param {Record<string, unknown>|null|undefined} messages
 * @param {string} key
 * @returns {string}
 */
function pick(messages, key) {
  const value = messages && typeof messages === "object" ? messages[key] : undefined;
  return typeof value === "string" && value ? value : FALLBACK[key];
}

/**
 * 取置きの表示にする状態か。
 *
 * @param {Object|null|undefined} state REST `/sites/batch` の 1 件、または ProductState 相当。
 * @returns {boolean}
 */
export function isHoldState(state) {
  if (!state || typeof state !== "object") {
    return false;
  }
  if (state.status === "HELD") {
    return true;
  }
  return state.status === "RESERVED" && state.hold_active === true;
}

/**
 * 取置き中の案内文。取置きでなければ空文字。
 *
 * @param {Object} state `status` / `hold_active` / `hold_hours_left`（int|null）を持つ。
 * @param {Record<string, unknown>} [messages] `window.SITEPICK.messages`（無ければ既定の文）。
 * @param {"detail"|"card"} [context] "detail"（長い文）| "card"（カード・比較表の短い形）。
 * @returns {string}
 */
export function holdNoticeText(state, messages = {}, context = "detail") {
  if (!isHoldState(state)) {
    return "";
  }
  const card = context === "card";
  const hours = state.hold_hours_left;
  if (!Number.isInteger(hours)) {
    return pick(messages, card ? "holdNoticeCardUnknown" : "holdNoticeUnknown");
  }
  if (hours <= 1) {
    return pick(messages, card ? "holdNoticeCardSoon" : "holdNoticeSoon");
  }
  return pick(messages, card ? "holdNoticeCard" : "holdNotice").replace("%d", String(hours));
}
